/* ================================================= */
/* PASTORAL MEMORY ENGINE                            */
/* Pure deterministic intelligence — no AI, no ML   */
/* Derives pastoral context from visit history,     */
/* prayer patterns, and current entity state.       */
/*                                                   */
/* Designed for future extension into:              */
/* - AI-generated pastoral summaries                */
/* - Smart visit scheduling                         */
/* - Predictive deterioration alerts                */
/* - Automated follow-up queues                     */
/* ================================================= */

import { getDaysSince } from "./date";

/* ============================= */
/* TREND TYPES                   */
/* ============================= */

export const TREND_TYPES = {
  IMPROVING:        "improving",
  RECOVERING:       "recovering",
  STABLE_HEALTHY:   "stable_healthy",
  DECLINING:        "declining",
  STABLE_NEGLECTED: "stable_neglected",
  UNKNOWN:          "unknown",
};

/* ============================= */
/* MEMORY PATTERN TYPES          */
/* Longitudinal signals          */
/* ============================= */

export const MEMORY_PATTERN_TYPES = {
  REPEATED_NO_VISIT:       "REPEATED_NO_VISIT",       // never or rarely visited
  CHRONIC_PRAYER_PATTERN:  "CHRONIC_PRAYER_PATTERN",  // persistent active prayer cluster
  RECOVERING:              "RECOVERING",               // recent visits after long absence
  IMPROVING_ACTIVITY:      "IMPROVING_ACTIVITY",       // visit frequency increasing
  DECLINING_ACTIVITY:      "DECLINING_ACTIVITY",       // visit frequency decreasing
  PERSISTENT_RISK:         "PERSISTENT_RISK",          // consistently neglected with history
  STABLE_HEALTH:           "STABLE_HEALTH",            // consistently healthy over time
  LOW_SPIRITUAL_CONCERN:   "LOW_SPIRITUAL_CONCERN",    // spiritual score critically low
};

/* ============================= */
/* RECOMMENDATION MESSAGES       */
/* Static — no interpolation     */
/* Future: feed into AI prompts  */
/* ============================= */

const RECOMMENDATION_MESSAGES = {
  [MEMORY_PATTERN_TYPES.RECOVERING]:
    { message: "Continuar el seguimiento — la entidad muestra recuperación reciente",    category: "visit",    severity: "positive" },
  [MEMORY_PATTERN_TYPES.STABLE_HEALTH]:
    { message: "Seguimiento constante y saludable — mantener el ritmo actual",           category: "visit",    severity: "positive" },
  [MEMORY_PATTERN_TYPES.IMPROVING_ACTIVITY]:
    { message: "El ritmo de visitas muestra mejoría reciente",                           category: "visit",    severity: "positive" },
  [MEMORY_PATTERN_TYPES.DECLINING_ACTIVITY]:
    { message: "El tiempo entre visitas está aumentando — conviene retomar el ritmo",    category: "visit",    severity: "medium"   },
  [MEMORY_PATTERN_TYPES.REPEATED_NO_VISIT]:
    { message: "Sin historial de visitas — iniciar seguimiento pastoral",                category: "visit",    severity: "critical" },
  [MEMORY_PATTERN_TYPES.PERSISTENT_RISK]:
    { message: "Ausencia recurrente — considerar intervención pastoral sostenida",       category: "visit",    severity: "high"     },
  [MEMORY_PATTERN_TYPES.CHRONIC_PRAYER_PATTERN]:
    { message: "Múltiples oraciones activas — revisar y acompañar espiritualmente",     category: "prayer",   severity: "high"     },
  [MEMORY_PATTERN_TYPES.LOW_SPIRITUAL_CONCERN]:
    { message: "Estado espiritual bajo — se recomienda acompañamiento pastoral",         category: "spiritual", severity: "medium"  },
};

/* ============================= */
/* HELPERS                       */
/* ============================= */

// Returns an ISO date string N days before today (avoids DST via ms arithmetic)
function daysAgoStr(n) {
  return new Date(Date.now() - n * 864e5).toISOString().split("T")[0];
}

/* ============================= */
/* MOMENTUM MODEL                */
/* ============================= */

/**
 * Computes pastoral momentum from visit history and current gap.
 *
 * Trend derivation:
 *  RECOVERING       — currentGap ≤ 21 but previous visit was > 60 days ago
 *  STABLE_HEALTHY   — currentGap ≤ 21 AND ≥ 3 visits in last 90 days
 *  IMPROVING        — currentGap < 60% of avg cadence (getting more frequent)
 *  STABLE_NEGLECTED — currentGap > 90 AND no visits in last 90 days
 *  DECLINING        — currentGap > 150% of avg cadence AND > 45 days
 *  UNKNOWN          — insufficient data
 *
 * Confidence scales with visit count (more data → higher confidence).
 * Designed as input for future AI scheduling and predictive alerts.
 *
 * @param {Object[]} visits        — sorted by date desc, each has { date }
 * @param {{ currentDaysSince }}   — days since most recent visit (null = never visited)
 * @returns {MomentumResult}
 */
export function computePastoralMomentum(visits, { currentDaysSince } = {}) {
  const EMPTY = {
    trend:        TREND_TYPES.UNKNOWN,
    confidence:   0,
    label:        "Historial insuficiente",
    visits_90d:   0,
    visits_30d:   0,
    avg_cadence:  null,
  };

  if (
    !visits ||
    visits.length === 0 ||
    currentDaysSince === null ||
    currentDaysSince === undefined
  ) {
    return EMPTY;
  }

  const d90Str = daysAgoStr(90);
  const d30Str = daysAgoStr(30);

  const datedVisits = visits
    .filter((v) => !!v.date)
    .map((v) => v.date)
    .sort((a, b) => b.localeCompare(a)); // newest first

  const visits_90d = datedVisits.filter((d) => d >= d90Str).length;
  const visits_30d = datedVisits.filter((d) => d >= d30Str).length;

  // Average cadence: mean gap between consecutive visits (up to 5 intervals)
  let avg_cadence = null;
  if (datedVisits.length >= 2) {
    const gaps = [];
    for (let i = 0; i < Math.min(datedVisits.length - 1, 5); i++) {
      const newerDays = getDaysSince(datedVisits[i]);
      const olderDays = getDaysSince(datedVisits[i + 1]);
      const gap = olderDays - newerDays;
      if (gap > 0) gaps.push(gap);
    }
    if (gaps.length > 0) {
      avg_cadence = Math.round(
        gaps.reduce((s, g) => s + g, 0) / gaps.length
      );
    }
  }

  // Confidence grows with available data (saturates at 5+ visits)
  const conf = Math.min(datedVisits.length / 5, 1);

  // RECOVERING: was absent 60+ days, but last visit is recent (≤ 21 days)
  if (currentDaysSince <= 21 && datedVisits.length >= 2) {
    const prevGap = getDaysSince(datedVisits[1]);
    if (prevGap > 60) {
      return {
        trend:       TREND_TYPES.RECOVERING,
        confidence:  Math.max(conf, 0.6),
        label:       "Recuperación: seguimiento retomado tras un período de ausencia",
        visits_90d,
        visits_30d,
        avg_cadence,
      };
    }
  }

  // STABLE_HEALTHY: short current gap AND consistent recent history
  if (currentDaysSince <= 21 && visits_90d >= 3) {
    return {
      trend:       TREND_TYPES.STABLE_HEALTHY,
      confidence:  Math.max(conf, 0.8),
      label:       "Seguimiento constante y saludable",
      visits_90d,
      visits_30d,
      avg_cadence,
    };
  }

  // IMPROVING: current gap is significantly shorter than historical average
  if (avg_cadence && currentDaysSince < avg_cadence * 0.6 && currentDaysSince <= 45) {
    return {
      trend:       TREND_TYPES.IMPROVING,
      confidence:  conf,
      label:       "Frecuencia de visitas en mejoría respecto al historial",
      visits_90d,
      visits_30d,
      avg_cadence,
    };
  }

  // STABLE_NEGLECTED: no visits in 90 days and current gap confirms absence
  if (currentDaysSince > 90 && visits_90d === 0) {
    return {
      trend:       TREND_TYPES.STABLE_NEGLECTED,
      confidence:  Math.max(conf, 0.7),
      label:       "Sin visitas registradas en los últimos 3 meses",
      visits_90d,
      visits_30d,
      avg_cadence,
    };
  }

  // DECLINING: current gap is substantially longer than historical cadence
  if (avg_cadence && currentDaysSince > avg_cadence * 1.5 && currentDaysSince > 45) {
    return {
      trend:       TREND_TYPES.DECLINING,
      confidence:  conf,
      label:       "El tiempo entre visitas está aumentando respecto al historial",
      visits_90d,
      visits_30d,
      avg_cadence,
    };
  }

  return {
    trend:       TREND_TYPES.UNKNOWN,
    confidence:  Math.min(conf * 0.5, 0.4),
    label:       "Historial en evaluación",
    visits_90d,
    visits_30d,
    avg_cadence,
  };
}

/* ============================= */
/* PATTERN DETECTION             */
/* Longitudinal signal detection */
/* ============================= */

/**
 * Detects pastoral patterns from visit history and current entity state.
 * Patterns are derived deterministically — no probability model.
 *
 * Each pattern: { type, confidence, severity }
 * Severity: "critical" | "high" | "medium" | "positive"
 *
 * Future: pattern array is the input contract for AI recommendation generation.
 *
 * @param {Object[]} visits
 * @param {{ currentDaysSince, urgentPrayers, activePrayers, spiritualScore }}
 * @returns {Object[]} patterns
 */
export function detectPastoralPatterns(
  visits,
  { currentDaysSince = null, urgentPrayers = 0, activePrayers = 0, spiritualScore = null } = {}
) {
  const momentum = computePastoralMomentum(visits, { currentDaysSince });
  const patterns = [];

  // Trend-derived patterns
  switch (momentum.trend) {
    case TREND_TYPES.RECOVERING:
      patterns.push({ type: MEMORY_PATTERN_TYPES.RECOVERING, confidence: momentum.confidence, severity: "positive" });
      break;
    case TREND_TYPES.STABLE_HEALTHY:
      patterns.push({ type: MEMORY_PATTERN_TYPES.STABLE_HEALTH, confidence: momentum.confidence, severity: "positive" });
      break;
    case TREND_TYPES.IMPROVING:
      patterns.push({ type: MEMORY_PATTERN_TYPES.IMPROVING_ACTIVITY, confidence: momentum.confidence, severity: "positive" });
      break;
    case TREND_TYPES.DECLINING:
      patterns.push({ type: MEMORY_PATTERN_TYPES.DECLINING_ACTIVITY, confidence: momentum.confidence, severity: "medium" });
      break;
    case TREND_TYPES.STABLE_NEGLECTED: {
      const hasHistory = visits && visits.length > 1;
      patterns.push({
        type:       hasHistory ? MEMORY_PATTERN_TYPES.PERSISTENT_RISK : MEMORY_PATTERN_TYPES.REPEATED_NO_VISIT,
        confidence: momentum.confidence,
        severity:   hasHistory ? "high" : "critical",
      });
      break;
    }
    default:
      // UNKNOWN — no trend-based pattern; check for initial absence
      if ((!visits || visits.length === 0) && currentDaysSince === null) {
        patterns.push({ type: MEMORY_PATTERN_TYPES.REPEATED_NO_VISIT, confidence: 0.9, severity: "critical" });
      }
      break;
  }

  // Independent: chronic prayer cluster (urgency + volume = care burden)
  if (urgentPrayers > 0 && activePrayers >= 2) {
    patterns.push({ type: MEMORY_PATTERN_TYPES.CHRONIC_PRAYER_PATTERN, confidence: 0.8, severity: "high" });
  }

  // Independent: spiritual concern (score-based, not trend-based)
  if (spiritualScore !== null && spiritualScore <= 2) {
    patterns.push({ type: MEMORY_PATTERN_TYPES.LOW_SPIRITUAL_CONCERN, confidence: 0.9, severity: "medium" });
  }

  return patterns;
}

/* ============================= */
/* RECOMMENDATION ENGINE         */
/* ============================= */

/**
 * Converts patterns into human-readable pastoral recommendations.
 * Sorted: critical → high → medium → positive.
 *
 * Recommendation shape:
 * { key, message, confidence, severity, category }
 *
 * Future: this array is the structured prompt context for AI-generated
 * pastoral summaries and assistant suggestions.
 */
export function buildRecommendationSet(patterns) {
  const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, positive: 3 };

  return patterns
    .filter((p) => RECOMMENDATION_MESSAGES[p.type])
    .map((p) => ({
      key:        p.type,
      message:    RECOMMENDATION_MESSAGES[p.type].message,
      category:   RECOMMENDATION_MESSAGES[p.type].category,
      severity:   RECOMMENDATION_MESSAGES[p.type].severity,
      confidence: p.confidence,
    }))
    .sort(
      (a, b) =>
        (SEVERITY_ORDER[a.severity] ?? 4) - (SEVERITY_ORDER[b.severity] ?? 4)
    );
}

/* ============================= */
/* MAIN ENTRY POINT              */
/* ============================= */

/**
 * buildPastoralMemory — unified pastoral memory for any entity.
 *
 * @param {Object[]} visits — full visit history, sorted by date desc
 * @param {{
 *   currentDaysSince?: number|null,
 *   urgentPrayers?:    number,
 *   activePrayers?:    number,
 *   spiritualScore?:   number|null,
 * }} options
 * @returns {{ momentum, patterns, recommendations }}
 *
 * Memory shape is the stable input contract for:
 * - PastoralInsight UI component
 * - Future AI assistant context building
 * - Smart scheduling engine (use avg_cadence for optimal visit timing)
 * - Predictive deterioration: feed trend + confidence into alert escalation
 */
export function buildPastoralMemory(visits, options = {}) {
  const {
    currentDaysSince = null,
    urgentPrayers    = 0,
    activePrayers    = 0,
    spiritualScore   = null,
  } = options;

  const momentum        = computePastoralMomentum(visits, { currentDaysSince });
  const patterns        = detectPastoralPatterns(visits, { currentDaysSince, urgentPrayers, activePrayers, spiritualScore });
  const recommendations = buildRecommendationSet(patterns);

  return { momentum, patterns, recommendations };
}
